
/**
 * The Singleton class is designed to manage singleton instances, where each instance is uniquely identified by a key.
 * It provides mechanisms for creating and retrieving singleton instances and ensures that each key can only
 * have one corresponding instance.
 */
export class Singleton {
  static _shared = null;
  static _locked = true;

  /**
   * Instances of the class are managed internally using the create method.
   */
  constructor() {
    if (Singleton._locked) {
      throw new Error('Singleton cannot be instantiated directly.');
    }
    this._instances = new Map();
  }

  /**
   * This method is used to create a new singleton instance or retrieve
   * an existing one associated with a unique key.
   * @param {*} key The unique identifier for the singleton instance.
   * @param {*} createFn A function that creates and returns a new instance. This function is called only if an instance for the given key does not already exist.
   * @returns
   */
  create(key, createFn) {
    if (key == 'shared' || key == 'create') {
      throw new Error('Invalid singleton key');
    }

    if (typeof createFn !== 'function') {
      throw new Error('createFn must be a valid function');
    }

    if (!this._instances.has(key)) {
      const instance = createFn();
      this._instances.set(key, instance);
      this[key] = instance;
    }

    return this._instances.get(key);
  }

  /**
   * This ensures that only one Singleton instance is used globally, following the singleton pattern.
   */
  static get shared() {
    if (Singleton._shared == null) {
      Singleton._locked = false;
      Singleton._shared = new Singleton();
      Singleton._locked = true;
    }
    return Singleton._shared;
  }
}
